import {
  Box,
  BoxProps,
  Skeleton,
  Stack,
  StackProps,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChatMessageGroup,
  ConversationChannelTypeExtended,
  ParticipationBadge,
  TransformedMessage,
} from '@provision/chat-utils';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
} from '@provision/conversation-types';
import { getFullName } from '@provision/formatting-utils';
import { RichTextEditorFile } from '@provision/rich-text-editor';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { DynamicIcon, PartnerTimeZoneTooltip, PlatformUserAvatar } from 'src/components';
import { StyledAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { ConversationParticipant, DELETED_CLIENT_TEXT, Partner } from 'src/graphql';
import {
  DEACTIVATED_USER_COLOR,
  DEACTIVATED_USER_TEXT,
  useCurrentUserProfile,
} from 'src/graphql/entities/platform-user';
import {
  UserPresence,
  UserPresenceStatus,
} from 'src/graphql/entities/user-presence/user-presence';
import { ParticipantSelectorItem } from 'src/pages/Conversations/Chat/ParticipationPanel/types';
import { getParticipantCustomIcon } from 'src/pages/Conversations/Chat/utils';
import {
  getConversationIcon,
  getConversationIconLibrary,
} from 'src/pages/Conversations/constants';
import { useDisplayTime } from 'src/utils/hooks';
import { randomFloat } from 'src/utils/number';
import { Message } from './chat-message';
import { UserInfoTooltip } from './user-info-tooltip';

const OuterGroupContainer = styled(Stack, { name: 'OuterGroupContainer' })(({ theme }) => ({
  gap: theme.spacing(0.5),
  padding: 0,
  flexShrink: 0,
}));

const InnerGroupContainer = styled(Stack, {
  name: 'OuterGroupContainer',
})(({ theme }) => {
  return {
    padding: theme.spacing(0.5),
    backgroundColor: 'inherit',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    width: '100%',
    alignItems: 'flex-start',
  };
});

const SenderInfoContainer = styled(Stack, { name: 'SenderInfoContainer' })(({ theme }) => {
  return {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(0.5),

    '& > *': {
      order: 1,
    },
  };
});

const GroupIcon = ({
  channel,
  ...rest
}: { channel: ConversationChannelTypeExtended } & BoxProps) => {
  return (
    <DynamicIcon
      name={getConversationIcon(channel)}
      library={getConversationIconLibrary(channel)}
      size={14}
      {...rest}
      sx={{
        fontSize: '0.875rem',
        width: '0.875rem',
        height: '0.875rem',
        lineHeight: '0.875rem',
        color: (theme) => theme.palette.custom.OV.sys.outline,
        ...rest.sx,
      }}
    />
  );
};

// Internal notes spell out what they are instead of relying on the icon alone —
// mistaking a note for a public reply has real consequences (1V-5641).
const NoteBylineLabel = () => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.5}
      mx={0.25}
      sx={{ color: (theme) => theme.palette.custom.OV.extended.Signature.color }}
    >
      <GroupIcon
        channel={ConversationChannelTypeExtended.InternalNote}
        sx={{ color: 'inherit' }}
      />
      <Typography fontSize="0.6875rem" fontWeight={500} lineHeight={1} color="inherit">
        Internal Note
      </Typography>
    </Stack>
  );
};

const SenderInfo = ({
  entity,
  sender,
  date,
  channel,
  partnerId,
  badgeConfig,
  isUserActive,
  isOnline,
  partner,
  senderTz,
  senderUserGroups,
  editedAtLabel,
}: {
  entity: ConversationParticipantEntity;
  sender: ConversationParticipant | null;
  date: string;
  partnerId?: number | null;
  channel: ConversationChannelTypeExtended;
  isUserActive: boolean;
  badgeConfig?: ParticipationBadge;
  isOnline?: boolean;
  partner?: Partner;
  senderTz: string;
  editedAtLabel?: React.ReactNode;
  senderUserGroups: { id: string; name: string }[];
}) => {
  const theme = useTheme();
  const showCustomIcon =
    entity === ConversationParticipantEntity.Bot ||
    entity === ConversationParticipantEntity.Rsm;
  const customIconName = getParticipantCustomIcon(entity) || '';

  const { displayTime } = useDisplayTime();

  const placeholderLength = useMemo(() => randomFloat(3, 7), []);

  return (
    <SenderInfoContainer>
      <Typography fontWeight="bold" fontSize="0.6875rem" ml={0.5}>
        {sender ? (
          getFullName(sender)
        ) : (
          <Skeleton sx={{ width: `${placeholderLength}rem`, height: '1.4rem' }} />
        )}
      </Typography>
      {sender &&
      entity === ConversationParticipantEntity.Client &&
      'deletedAt' in sender &&
      sender.deletedAt ? (
        <Typography
          fontWeight="bold"
          fontSize="0.6875rem"
          ml={0.5}
          sx={{ marginLeft: 0 }}
          color={DEACTIVATED_USER_COLOR}
        >
          {`(${DELETED_CLIENT_TEXT})`}
        </Typography>
      ) : null}
      {sender && !isUserActive ? (
        <Typography
          fontWeight="bold"
          fontSize="0.6875rem"
          ml={0.5}
          sx={{
            marginLeft: 0,
          }}
          color={DEACTIVATED_USER_COLOR}
        >
          {`(${DEACTIVATED_USER_TEXT})`}
        </Typography>
      ) : null}
      {channel === ConversationChannelTypeExtended.InternalNote ? (
        <NoteBylineLabel />
      ) : (
        <GroupIcon mx={0.25} channel={channel} />
      )}
      <Typography
        fontWeight="bold"
        fontSize="0.6875rem"
        mr={0.5}
        sx={{ color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant }}
      >
        <PartnerTimeZoneTooltip date={date} partnerId={partnerId}>
          <Stack direction="row" gap={1}>
            {displayTime(date)} {editedAtLabel}
          </Stack>
        </PartnerTimeZoneTooltip>
      </Typography>
      {entity === ConversationParticipantEntity.PlatformUser ? (
        <UserInfoTooltip
          senderTz={senderTz}
          senderUserGroups={senderUserGroups}
          sender={sender}
          partner={partner}
          partnerId={partnerId}
          isOnline={isOnline}
        >
          <span>
            {showCustomIcon ? (
              <StyledAvatar
                sx={{ order: 0, margin: '0 0.25rem' }}
                backgroundColor={theme.palette.custom.OV.sys.primary}
              >
                <DynamicIcon name={customIconName} sx={{ width: 'unset', height: 'unset' }} />
              </StyledAvatar>
            ) : (
              <PlatformUserAvatar
                name={getFullName(sender)}
                partnerId={sender && 'partnerId' in sender ? sender.partnerId : undefined}
                avatarFileKey={
                  sender && 'avatarFileKey' in sender ? sender.avatarFileKey : undefined
                }
                badgeConfig={badgeConfig}
                online={!!isOnline}
                sx={{ order: 0, margin: '0 0.25rem' }}
              />
            )}
          </span>
        </UserInfoTooltip>
      ) : (
        <span style={{ display: 'inline-flex', order: 0 }}>
          {showCustomIcon ? (
            <StyledAvatar
              sx={{ order: 0, margin: '0 0.25rem' }}
              backgroundColor={theme.palette.custom.OV.sys.primary}
            >
              <DynamicIcon name={customIconName} sx={{ width: 'unset', height: 'unset' }} />
            </StyledAvatar>
          ) : (
            <PlatformUserAvatar
              name={getFullName(sender)}
              partnerId={sender && 'partnerId' in sender ? sender.partnerId : undefined}
              avatarFileKey={
                sender && 'avatarFileKey' in sender ? sender.avatarFileKey : undefined
              }
              badgeConfig={badgeConfig}
              online={!!isOnline}
              sx={{ order: 0, margin: '0 0.25rem' }}
            />
          )}
        </span>
      )}
    </SenderInfoContainer>
  );
};

const ChatMessagesGroupComponent = ({
  group,
  conversationChannel,
  receiver,
  onUpdateMessage,
  onEditMessage,
  readonly = false,
  reactionsEnabled = true,
  sx,
  senderPresence,
  partner,
  metaData,
}: {
  receiver?: ParticipantSelectorItem;
  group: ChatMessageGroup<ConversationParticipant>;
  conversationChannel?: ConversationChannelTypeExtended | null;
  onUpdateMessage: (message: { conversationMessageId: string; body: string }) => void;
  onEditMessage?: (
    newMessage: {
      message: string;
      attachments: RichTextEditorFile[];
      editedAt: string;
    },
    originalMessage: TransformedMessage,
  ) => void;
  readonly?: boolean;
  reactionsEnabled?: boolean;
  sx?: StackProps['sx'];
  senderPresence?: UserPresence;
  partner?: Partner;
  metaData?: Record<string, Record<string, unknown[]>>;
}) => {
  const { user } = useCurrentUserProfile();
  const { displayTime } = useDisplayTime();

  const { messages, sender, senderEntity, channelType } = group;

  const uniqueMessages = useMemo(
    () =>
      messages
        // Remove duplicated elements
        ?.filter((message, index, arr) => arr.findIndex((m) => m.id === message.id) === index),
    [messages],
  );

  const channel = useMemo(() => {
    return channelType !== ConversationChannelTypeExtended.InternalNote && conversationChannel
      ? conversationChannel
      : channelType;
  }, [conversationChannel, channelType]);

  const editedAtLabel = useMemo(() => {
    let mostRecentEditedAt: string | null = null;
    uniqueMessages.forEach((message) => {
      if (message.editedAt) {
        if (!mostRecentEditedAt || new Date(message.editedAt) > new Date(mostRecentEditedAt)) {
          mostRecentEditedAt = message.editedAt;
        }
      }
    });
    const isToday = mostRecentEditedAt
      ? dayjs(mostRecentEditedAt).isSame(dayjs(), 'day')
      : false;
    return mostRecentEditedAt ? (
      <Box component="span" fontStyle="italic" fontWeight="normal">
        Edited{' '}
        {`${isToday ? 'Today' : dayjs(mostRecentEditedAt).format('MMM Do, YYYY')} ${displayTime(mostRecentEditedAt)}`}
      </Box>
    ) : (
      ''
    );
  }, [displayTime, uniqueMessages]);

  const handleEditMessage = useCallback(
    (messageId: string, newBody: string, newAttachments: RichTextEditorFile[]) => {
      const originalMessage = messages.find((m) => m.id === messageId);
      if (!originalMessage) {
        return;
      }
      onEditMessage?.(
        {
          message: newBody,
          attachments: newAttachments,
          editedAt: new Date().toISOString(),
        },
        originalMessage,
      );
    },
    [messages, onEditMessage],
  );

  return (
    <OuterGroupContainer
      alignItems="flex-start"
      sx={sx}
      data-chat-group-id={group.id}
      data-chat-group-created-at={group.createdAt}
      data-testid="chat-messages-group"
    >
      <InnerGroupContainer>
        <SenderInfo
          senderTz={senderPresence?.tz || ''}
          senderUserGroups={senderPresence?.assigneeGroups || []}
          partner={partner}
          isOnline={
            senderPresence?.userId === user?.id ||
            senderPresence?.status === UserPresenceStatus.active
          }
          channel={channel}
          sender={sender}
          entity={senderEntity}
          date={group.createdAt}
          partnerId={messages[0]?.partnerId}
          badgeConfig={group.badgeConfig}
          isUserActive={!!group.isUserActive}
          editedAtLabel={editedAtLabel}
        />
        {uniqueMessages.map((message) => (
          <Message
            key={message.id}
            id={message.id}
            partnerId={message.partnerId}
            type="text"
            text={message.message}
            createdAt={message.createdAt}
            attachments={message.attachments}
            channel={channel}
            messageType={message.type!}
            emailFileKey={message.emailFileKey}
            onUpdate={onUpdateMessage}
            onEdit={handleEditMessage}
            readonly={readonly}
            reactionsEnabled={reactionsEnabled}
            sender={sender}
            isEditable={message.senderId === user?.id && !!message.isPrivate}
            receiver={receiver}
            metaData={metaData}
            messageDispatches={message.messageDispatches?.filter(
              (i) =>
                (i.participation?.channel
                  ?.conversationChannelTypeId as ConversationChannelType) ===
                ConversationChannelType.Email,
            )}
          />
        ))}
      </InnerGroupContainer>
    </OuterGroupContainer>
  );
};

export const ChatMessagesGroup = memo(ChatMessagesGroupComponent);
